import { useCallback, useEffect, useRef, useState } from "react";

import { IoChevronBack, IoChevronForward } from "react-icons/io5";

import { fetchPlaceDetail } from "@/apis/admin";
import type { PlaceDetail, PlaceImage } from "@/types/place";

interface Props {
  plcCd: string;
}

const IMAGE_BASE = import.meta.env.VITE_API_BASE_URL || "";

const PlaceInfoCard = ({ plcCd }: Props) => {
  const [detail, setDetail] = useState<PlaceDetail | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);

  useEffect(() => {
    setLoading(true);
    setCurrentIdx(0);
    fetchPlaceDetail(plcCd)
      .then((data) => setDetail(data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [plcCd]);

  const imageCount = detail?.images?.length || 0;

  const goPrev = useCallback(() => setCurrentIdx((p) => Math.max(0, p - 1)), []);
  const goNext = useCallback(() => setCurrentIdx((p) => Math.min(imageCount - 1, p + 1)), [imageCount]);

  // 키보드 좌우 방향키
  useEffect(() => {
    if (imageCount <= 1) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [imageCount, goPrev, goNext]);

  // 터치/마우스 드래그
  const handlePointerDown = (e: React.PointerEvent) => {
    touchStartX.current = e.clientX;
    touchDeltaX.current = 0;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (touchStartX.current === 0) return;
    touchDeltaX.current = e.clientX - touchStartX.current;
  };

  const handlePointerUp = () => {
    const threshold = 50;
    if (touchDeltaX.current > threshold) goPrev();
    else if (touchDeltaX.current < -threshold) goNext();
    touchStartX.current = 0;
    touchDeltaX.current = 0;
  };

  if (loading) {
    return (
      <div className="flex h-16 items-center justify-center text-sm text-gray-400">
        장소 정보 불러오는 중...
      </div>
    );
  }

  if (!detail) {
    return (
      <p className="text-sm text-gray-400">장소 상세 정보가 아직 등록되지 않았습니다.</p>
    );
  }

  const { images, description, plcNm } = detail;
  const hasImages = images && images.length > 0;
  const hasDescription = description && description.trim();

  return (
    <div className="flex flex-col gap-3">
      {/* 장소명 */}
      <div className="text-sm">
        <span className="font-medium text-gray-500">장소명 : </span>
        <span className="font-semibold text-gray-800">{plcNm}</span>
      </div>

      {/* 이미지 슬라이더 */}
      {hasImages && (
        <div
          ref={sliderRef}
          className="relative select-none overflow-hidden rounded-lg"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{ touchAction: "pan-y" }}
        >
          <div
            className="flex h-full transition-transform duration-300"
            style={{ transform: `translateX(-${currentIdx * 100}%)` }}
          >
            {images.map((img: PlaceImage) => (
              <img
                key={img.imgId}
                src={`${IMAGE_BASE}/uploads/places/${plcCd}/${img.fileNm}`}
                alt={img.origNm}
                className="w-full shrink-0 rounded-lg object-contain max-h-96 pointer-events-none"
                draggable={false}
              />
            ))}
          </div>
          {images.length > 1 && (
            <>
              {/* 좌측 영역 클릭 → 이전 */}
              {currentIdx > 0 && (
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-0 top-0 flex h-full w-1/3 cursor-pointer items-center justify-start pl-1"
                >
                  <span className="rounded-full bg-black/30 p-1 text-white hover:bg-black/50">
                    <IoChevronBack size={16} />
                  </span>
                </button>
              )}
              {/* 우측 영역 클릭 → 다음 */}
              {currentIdx < images.length - 1 && (
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-0 top-0 flex h-full w-1/3 cursor-pointer items-center justify-end pr-1"
                >
                  <span className="rounded-full bg-black/30 p-1 text-white hover:bg-black/50">
                    <IoChevronForward size={16} />
                  </span>
                </button>
              )}
              <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
                {images.map((_: PlaceImage, i: number) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setCurrentIdx(i)}
                    className={`h-2 w-2 rounded-full transition-colors ${
                      i === currentIdx ? "bg-white" : "bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* 주의사항 */}
      {hasDescription && (
        <div>
          <div className="mb-1 text-xs font-medium text-gray-500">주의사항</div>
          <p className="whitespace-pre-wrap rounded-lg bg-amber-50 px-3 py-2 text-sm leading-relaxed text-gray-700">
            {description}
          </p>
        </div>
      )}

      {!hasImages && !hasDescription && (
        <p className="text-sm text-gray-400">등록된 장소 정보가 없습니다.</p>
      )}
    </div>
  );
};

export default PlaceInfoCard;
